import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const graphicRef = useRef(null);
  const ctaRef = useRef(null);
  const statsRef = useRef(null);
  const orbRef1 = useRef(null);
  const orbRef2 = useRef(null);
  const orbRef3 = useRef(null);
  const gridRef = useRef(null);
  const taglineRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Master timeline
      const tl = gsap.timeline({ delay: 2.5 });

      // Title animation - letter by letter
      const titleChars = titleRef.current.querySelectorAll(".char");
      tl.from(titleChars, {
        y: 200,
        opacity: 0,
        rotateX: -90,
        stagger: 0.05,
        duration: 1.2,
        ease: "power4.out",
      });

      // Subtitle animation
      tl.from(
        subtitleRef.current,
        {
          y: 50,
          opacity: 0,
          duration: 1,
          ease: "power3.out",
        },
        "-=0.5"
      );

      // Tagline reveal
      tl.from(
        taglineRef.current,
        {
          y: 40,
          opacity: 0,
          duration: 1,
          ease: "power3.out",
        },
        "-=0.7"
      );

      // Graphic animation
      tl.from(
        graphicRef.current,
        {
          scale: 1.5,
          opacity: 0,
          duration: 1.5,
          ease: "power3.out",
        },
        "-=0.8"
      );

      // CTA buttons animation
      tl.from(
        ctaRef.current.children,
        {
          y: 30,
          opacity: 0,
          stagger: 0.1,
          duration: 0.8,
          ease: "power3.out",
        },
        "-=0.5"
      );

      // Stats animation
      tl.from(
        statsRef.current.children,
        {
          y: 30,
          opacity: 0,
          stagger: 0.15,
          duration: 0.8,
          ease: "power3.out",
        },
        "-=0.4"
      );

      // Grid lines animation
      tl.from(
        gridRef.current,
        {
          opacity: 0,
          duration: 2,
          ease: "power2.out",
        },
        "-=1"
      );

      // Floating orbs continuous animation
      gsap.to(orbRef1.current, {
        y: -30,
        x: 15,
        duration: 4,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });

      gsap.to(orbRef2.current, {
        y: 25,
        x: -20,
        duration: 5,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        delay: 1,
      });

      gsap.to(orbRef3.current, {
        y: -20,
        x: 10,
        duration: 6,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        delay: 2,
      });

      // Parallax effect on scroll
      gsap.to(titleRef.current, {
        yPercent: 50,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });

      gsap.to(graphicRef.current, {
        yPercent: 30,
        scale: 1.1,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  // Split text into characters
  const splitText = (text) => {
    return text.split("").map((char, i) => (
      <span
        key={i}
        className="char inline-block"
        style={{ display: char === " " ? "inline" : "inline-block" }}
      >
        {char === " " ? "\u00A0" : char}
      </span>
    ));
  };

  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#0a0a0a]"
    >
      {/* Animated grid background */}
      <div
        ref={gridRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(201,255,0,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(201,255,0,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "80px 80px",
        }}
      />

      {/* Gradient orbs */}
      <div
        ref={orbRef1}
        className="absolute top-[10%] left-[5%] w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(201,255,0,0.08) 0%, transparent 70%)",
          filter: "blur(80px)",
        }}
      />
      <div
        ref={orbRef2}
        className="absolute bottom-[10%] right-[10%] w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(0,255,240,0.06) 0%, transparent 70%)",
          filter: "blur(100px)",
        }}
      />
      <div
        ref={orbRef3}
        className="absolute top-[40%] right-[30%] w-[300px] h-[300px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(201,255,0,0.05) 0%, transparent 70%)",
          filter: "blur(60px)",
        }}
      />

      {/* Main content */}
      <div className="relative z-10 px-6 md:px-12 lg:px-20 pt-32 pb-20">
        <div className="max-w-7xl mx-auto">
          {/* Subtitle badge */}
          <div ref={subtitleRef} className="mb-8 flex items-center gap-4">
            <span className="section-label" style={{ marginBottom: 0 }}>
              Digital Studio
            </span>
            <div className="hidden md:flex items-center gap-2 px-3 py-1 rounded-full border border-[#c9ff00]/20 bg-[#c9ff00]/5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#c9ff00] animate-pulse" />
              <span className="text-[10px] tracking-wider text-[#c9ff00] uppercase font-medium">
                Available Now
              </span>
            </div>
          </div>

          {/* Main title */}
          <div className="relative">
            <h1 ref={titleRef} className="hero-title mb-6">
              <span className="block">{splitText("BAYSTRIX")}</span>
              <span className="block">
                {splitText("STU")}
                <span className="char inline-block relative">
                  D
                  <svg
                    className="absolute -top-2 -right-3 md:-top-4 md:-right-6 w-6 h-6 md:w-10 md:h-10"
                    viewBox="0 0 40 40"
                    fill="none"
                  >
                    <circle cx="20" cy="20" r="8" fill="#c9ff00" />
                  </svg>
                </span>
                {splitText("IO")}
              </span>
            </h1>

            {/* Decorative line from title */}
            <div className="hidden lg:block absolute -right-12 top-1/2 -translate-y-1/2">
              <div className="w-[1px] h-32 bg-gradient-to-b from-[#c9ff00] to-transparent" />
            </div>
          </div>

          {/* Tagline */}
          <div ref={taglineRef} className="mt-6 mb-12">
            <p className="text-xl md:text-2xl lg:text-3xl text-white/60 font-display font-light tracking-tight max-w-2xl">
              Designing &{" "}
              <span className="text-gradient font-medium">Building</span>{" "}
              Digital Products That{" "}
              <span className="text-white font-medium">Drive Results</span>
            </p>
          </div>

          {/* CTA + Description */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-12 mt-8">
            <div ref={ctaRef} className="flex flex-wrap items-center gap-4">
              <a href="#contact" className="btn-primary magnetic">
                <span>Start a Project</span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 11L11 1M11 1H1M11 1V11"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                </svg>
              </a>
              <a href="#projects" className="btn-outline magnetic">
                <span>View Work</span>
              </a>
              <span className="hidden md:inline-block text-sm text-[#888] ml-4 max-w-xs leading-relaxed">
                Web apps, mobile apps & UI/UX — from concept to launch.
              </span>
            </div>

            {/* Stats strip */}
            <div
              ref={statsRef}
              className="flex items-center gap-8 lg:gap-12"
            >
              {[
                { value: "11+", label: "Projects" },
                { value: "3", label: "Services" },
                { value: "100%", label: "Dedication" },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-display font-bold text-[#c9ff00]">
                    {stat.value}
                  </div>
                  <div className="text-[10px] md:text-xs tracking-[0.2em] uppercase text-[#888] mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Abstract graphic - morphing shape composition */}
        <div
          ref={graphicRef}
          className="absolute top-1/2 right-0 -translate-y-1/2 w-[280px] h-[400px] md:w-[380px] md:h-[500px] lg:w-[480px] lg:h-[600px] pointer-events-none opacity-40 lg:opacity-100"
        >
          {/* Main shape */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              viewBox="0 0 400 500"
              fill="none"
              className="w-full h-full"
              style={{ filter: "drop-shadow(0 0 40px rgba(201,255,0,0.1))" }}
            >
              {/* Outer rotating ring */}
              <g className="origin-center" style={{ animation: "spin 20s linear infinite" }}>
                <circle cx="200" cy="250" r="180" stroke="rgba(201,255,0,0.08)" strokeWidth="1" strokeDasharray="8 12" />
              </g>
              {/* Middle ring */}
              <g className="origin-center" style={{ animation: "spin 30s linear infinite reverse" }}>
                <circle cx="200" cy="250" r="130" stroke="rgba(0,255,240,0.06)" strokeWidth="1" strokeDasharray="4 8" />
              </g>
              {/* Inner ring */}
              <circle cx="200" cy="250" r="80" stroke="rgba(201,255,0,0.12)" strokeWidth="1" />
              {/* Center glow */}
              <circle cx="200" cy="250" r="40" fill="rgba(201,255,0,0.04)" />
              <circle cx="200" cy="250" r="4" fill="rgba(201,255,0,0.6)" />

              {/* Orbital dots */}
              <g style={{ animation: "spin 15s linear infinite" }} className="origin-center">
                <circle cx="200" cy="70" r="3" fill="rgba(201,255,0,0.5)" />
                <line x1="200" y1="73" x2="200" y2="170" stroke="rgba(201,255,0,0.05)" strokeWidth="1" />
              </g>
              <g style={{ animation: "spin 25s linear infinite reverse" }} className="origin-center">
                <circle cx="330" cy="250" r="2.5" fill="rgba(0,255,240,0.5)" />
              </g>
              <g style={{ animation: "spin 18s linear infinite" }} className="origin-center">
                <circle cx="90" cy="350" r="2" fill="rgba(201,255,0,0.4)" />
              </g>

              {/* Geometric accent lines */}
              <line x1="200" y1="250" x2="350" y2="100" stroke="rgba(201,255,0,0.04)" strokeWidth="1" />
              <line x1="200" y1="250" x2="60" y2="380" stroke="rgba(0,255,240,0.03)" strokeWidth="1" />
              <line x1="200" y1="250" x2="340" y2="400" stroke="rgba(201,255,0,0.03)" strokeWidth="1" />

              {/* "BX" monogram in center */}
              <text
                x="200"
                y="260"
                textAnchor="middle"
                fill="rgba(201,255,0,0.08)"
                fontSize="48"
                fontFamily="Syne, sans-serif"
                fontWeight="800"
              >
                BX
              </text>
            </svg>
          </div>

          {/* Floating accent elements */}
          <div
            className="absolute top-[15%] right-[10%] w-12 h-12 rounded-lg border border-[#c9ff00]/10 bg-[#c9ff00]/5 flex items-center justify-center"
            style={{ animation: "float 6s ease-in-out infinite" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(201,255,0,0.3)" strokeWidth="1.5">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>
          <div
            className="absolute bottom-[20%] left-[5%] w-10 h-10 rounded-full border border-[#00fff0]/10 bg-[#00fff0]/5 flex items-center justify-center"
            style={{ animation: "float 5s ease-in-out infinite 2s" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(0,255,240,0.3)" strokeWidth="1.5">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 lg:px-20 pb-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Scroll indicator */}
          <div className="scroll-indicator">
            <span className="text-xs tracking-[0.3em] uppercase">Scroll</span>
            <div className="scroll-line" />
          </div>

          {/* Open for projects badge */}
          <div className="flex md:hidden items-center gap-3 px-4 py-2 rounded-full border border-[#222] glass">
            <span className="w-2 h-2 rounded-full bg-[#c9ff00] animate-pulse" />
            <span className="text-xs tracking-wider text-[#888] uppercase">
              Open for Projects
            </span>
          </div>
        </div>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        .origin-center {
          transform-origin: 200px 250px;
        }
      `}</style>
    </section>
  );
};

export default Hero;
